import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { z } from "zod";
import type { Permission } from "../shared/permissions";
import type { Role } from "./users.types";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import { api } from "./apiClient";
import { logError } from "./logError";
import { getToken } from "../auth/getToken";
import { queryClient } from "./queryClient";

const BASE_ENDPOINT_USERINFO = `${FORMS_API_BASE_ENDPOINT}/userInfo`;

export const supportedLanguages = ["sv", "no", "en"] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

export const isSupportedLanguage = (lang: any): lang is SupportedLanguage => supportedLanguages.includes(lang);

const SystemAnnouncementSchema = z.object({
  id: z.string(),
  title: z.string(),
  message: z.string(),
});
export type SystemAnnouncement = z.infer<typeof SystemAnnouncementSchema>;

export const PartnerSecurityTrialRequestStatusSchema = z.enum([
  "none",
  "pending",
  "active",
  "failed",
  "cancelled",
  "subscribed",
  "terminated",
]);
export type PartnerSecurityTrialRequestStatus = z.infer<typeof PartnerSecurityTrialRequestStatusSchema>;

const UserInfoOrganisationSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  systemAnnouncement: SystemAnnouncementSchema.optional(),
  permissions: z.array(z.custom<Permission>()).readonly(),
  lang: z.enum(supportedLanguages),
  roles: z.array(z.custom<Role>()).readonly(),
  dpiaEnabled: z.boolean(),
  classificationEnabled: z.boolean(),
  scimEnabled: z.boolean(),
  hubspotId: z.string().nullish(),
  partnerSecurityAndPrivacyProfileEnabled: z.boolean(),
  enabledFeatures: z.array(z.string()).readonly().optional(),
  partnerSecurityTrialRequestStatus: PartnerSecurityTrialRequestStatusSchema,
  isTestOrganization: z.boolean(),
});

export type UserInfoOrganisation = z.infer<typeof UserInfoOrganisationSchema>;

const UserInfoCustomerSchema = z.discriminatedUnion("customerKind", [
  UserInfoOrganisationSchema.extend({ customerKind: z.literal("basic").optional() }),
  UserInfoOrganisationSchema.extend({
    customerKind: z.literal("enterprise"),
    organisations: z.array(UserInfoOrganisationSchema),
  }),
]);
export type UserInfoCustomer = z.infer<typeof UserInfoCustomerSchema>;

const UserInfoSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  customers: z.array(UserInfoCustomerSchema),
  newsInfo: z.object({
    hasReadLatest: z.boolean(),
    newsReadDate: z.string().nullish(),
  }),
  userId: z.string(),
  isE2EUser: z.boolean().optional(),
  isInternalUser: z.boolean().optional(),
});
export type UserInfo = z.infer<typeof UserInfoSchema>;

export const userInfoKeys = { user: () => ["userInfo"] };

export const userInfoQueryOptions = () =>
  queryOptions({
    queryKey: userInfoKeys.user(),
    queryFn: () => api.get(`${BASE_ENDPOINT_USERINFO}`, { excludeCurrentOrganisationId: true, schema: UserInfoSchema }),
    throwOnError: true,
    /** Many components read user info. Don't refetch each time one mounts; coming back to the tab and invalidation still refresh it. */
    refetchOnMount: false,
  });

export const useUserInfo = () => useSuspenseQuery(userInfoQueryOptions()).data;

export const ensureUserInfo = () => {
  const hasToken = Boolean(getToken());
  /** We prefetch user info early in the app lifecycle. Since this runs before authentication is fully established, we must verify a token exists before attempting the request. */
  if (hasToken) {
    return queryClient
      .ensureQueryData(userInfoQueryOptions())
      .catch((error: Error) => logError(error, { loggedFrom: "ensureUserInfo" }));
  }
};
